import { Component, type ErrorInfo, type ReactNode, useState } from 'react';
import { telemetryService } from '@/services/telemetryService';
import {
  Button,
  Text,
  makeStyles,
  tokens,
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Tooltip,
  shorthands,
} from '@coreai/fluentui-react';
import {
  ArrowClockwiseRegular,
  ArrowCounterclockwiseRegular,
  ClipboardRegular,
  CheckmarkRegular,
  DismissRegular,
  BugRegular,
} from '@fluentui/react-icons';

/**
 * Error boundaries that catch React rendering errors and report them to
 * Application Insights. Two variants:
 * - AppErrorBoundary: Full-page fallback with reload/retry (use at root)
 * - FeatureErrorBoundary: Inline fallback for subsections (canvas, editor, etc.)
 */

type ErrorBoundaryVariant = 'app' | 'feature';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Component name for telemetry context */
  componentName?: string;
  /** Optional custom fallback UI */
  fallback?: ReactNode;
  /** @internal Controls fallback style and telemetry boundary label */
  variant?: ErrorBoundaryVariant;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorId: string | null;
}

const useStyles = makeStyles({
  /* ── App-level (full page) ────────────────────────────────── */
  appContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    width: '100vw',
    padding: tokens.spacingHorizontalXXL,
    backgroundColor: tokens.colorNeutralBackground1,
    backgroundImage: `radial-gradient(circle at 50% 0%, ${tokens.colorBrandBackground2} 0%, ${tokens.colorNeutralBackground1} 60%)`,
    overflow: 'hidden',
    position: 'relative',
  },
  glassCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: '600px',
    padding: '48px',
    ...shorthands.borderRadius('24px'),
    backgroundColor: tokens.colorNeutralBackgroundAlpha2,
    backdropFilter: 'blur(20px)',
    boxShadow: tokens.shadow16,
    zIndex: 1,
    position: 'relative',
    textAlign: 'center',
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStrokeAlpha2),
  },
  illustrationArea: {
    marginBottom: tokens.spacingVerticalXXXL,
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  iconWrapper: {
    width: '96px',
    height: '96px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    ...shorthands.borderRadius('50%'),
    fontSize: '48px',
    boxShadow: `0 0 0 12px ${tokens.colorBrandBackground2}40`,
  },
  appTitle: {
    fontSize: tokens.fontSizeHero800,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    lineHeight: tokens.lineHeightBase600,
    marginBottom: tokens.spacingVerticalL,
    letterSpacing: '-0.5px',
  },
  appMessage: {
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground2,
    lineHeight: '26px',
    marginBottom: tokens.spacingVerticalXXXL,
    maxWidth: '440px',
  },
  errorDetailsSection: {
    width: '100%',
    marginBottom: tokens.spacingVerticalXXXL,
    textAlign: 'left',
  },
  errorIdChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    padding: `${tokens.spacingVerticalSNudge} ${tokens.spacingHorizontalM}`,
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.border('1px', 'dashed', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    cursor: 'pointer',
    userSelect: 'all',
    transitionProperty: 'all',
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground2Hover,
      ...shorthands.borderColor(tokens.colorNeutralStroke1Hover),
      color: tokens.colorNeutralForeground2,
    },
  },
  buttonRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalL,
    width: '100%',
    justifyContent: 'center',
  },
  actionButton: {
    minWidth: '140px',
  },
  codeBlock: {
    backgroundColor: tokens.colorNeutralBackgroundInverted,
    color: tokens.colorNeutralForegroundInverted,
    padding: tokens.spacingVerticalL,
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: '200px',
    overflowY: 'auto',
    marginTop: tokens.spacingVerticalS,
    textAlign: 'left',
  },
  accordionHeader: {
    button: { padding: `${tokens.spacingVerticalS} 0` },
  },
  errorIdWrapper: {
    marginBottom: tokens.spacingVerticalXXXL,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalS,
  },
  retryButton: {
    marginTop: tokens.spacingVerticalS,
  },
  /* ── Background Decorations ────────────────────────────────── */
  decorBlob1: {
    position: 'absolute',
    top: '-10%',
    right: '-5%',
    width: '600px',
    height: '600px',
    background: `radial-gradient(circle, ${tokens.colorBrandBackground}20 0%, transparent 70%)`,
    opacity: 0.6,
    zIndex: 0,
    filter: 'blur(60px)',
    animationName: {
      '0%': { transform: 'translate(0, 0) scale(1)' },
      '50%': { transform: 'translate(-20px, 20px) scale(1.1)' },
      '100%': { transform: 'translate(0, 0) scale(1)' },
    },
    animationDuration: '10s',
    animationIterationCount: 'infinite',
    animationTimingFunction: tokens.curveEasyEase,
  },
  decorBlob2: {
    position: 'absolute',
    bottom: '-10%',
    left: '-5%',
    width: '500px',
    height: '500px',
    background: `radial-gradient(circle, ${tokens.colorPaletteBlueBorderActive}20 0%, transparent 70%)`,
    opacity: 0.5,
    zIndex: 0,
    filter: 'blur(50px)',
    animationName: {
      '0%': { transform: 'translate(0, 0) scale(1)' },
      '50%': { transform: 'translate(20px, -20px) scale(0.9)' },
      '100%': { transform: 'translate(0, 0) scale(1)' },
    },
    animationDuration: '12s',
    animationIterationCount: 'infinite',
    animationTimingFunction: tokens.curveEasyEase,
  },

  /* ── Feature-level (inline) ───────────────────────────────── */
  featureContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalXL,
    textAlign: 'center',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    minHeight: '160px',
    transitionProperty: 'all',
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground2Hover,
      ...shorthands.borderColor(tokens.colorNeutralStroke1Hover),
    },
  },
  featureIconRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground2,
    marginBottom: tokens.spacingVerticalXS,
  },
  featureTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  featureMessage: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    maxWidth: '280px',
  },
});

function generateErrorId(): string {
  return `ERR-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
}

const VARIANT_DEFAULTS: Record<ErrorBoundaryVariant, { componentName: string; boundary: string }> = {
  app: { componentName: 'AppErrorBoundary', boundary: 'react' },
  feature: { componentName: 'FeatureErrorBoundary', boundary: 'feature' },
};

/** Single error boundary class powering both AppErrorBoundary and FeatureErrorBoundary. */
export class AppErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorId: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error, errorId: generateErrorId() };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const defaults = VARIANT_DEFAULTS[this.props.variant ?? 'app'];
    telemetryService.trackException(error, {
      component: this.props.componentName ?? defaults.componentName,
      componentStack: errorInfo.componentStack ?? '',
      errorId: this.state.errorId ?? '',
      boundary: defaults.boundary,
    });
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorId: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      const variant = this.props.variant ?? 'app';
      if (variant === 'feature') {
        return <FeatureFallback onRetry={this.handleReset} />;
      }
      return <AppFallback error={this.state.error} errorId={this.state.errorId} onReload={this.handleReload} onRetry={this.handleReset} />;
    }
    return this.props.children;
  }
}

/** Convenience wrapper that defaults to the feature variant. */
export const FeatureErrorBoundary = ({ children, ...rest }: Omit<ErrorBoundaryProps, 'variant'>) => (
  <AppErrorBoundary variant="feature" {...rest}>
    {children}
  </AppErrorBoundary>
);

/* ── Fallback UIs ─────────────────────────────────────────────── */

type AppFallbackProps = {
  error: Error | null;
  errorId: string | null;
  onReload: () => void;
  onRetry: () => void;
};

const AppFallback = ({ error, errorId, onReload, onRetry }: AppFallbackProps) => {
  const styles = useStyles();
  const [copied, setCopied] = useState(false);

  const handleCopyErrorId = () => {
    if (errorId) {
      navigator.clipboard.writeText(errorId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className={styles.appContainer}>
      <div className={styles.decorBlob1} />
      <div className={styles.decorBlob2} />

      <div className={styles.glassCard}>
        <div className={styles.illustrationArea}>
          <div className={styles.iconWrapper}>
            <BugRegular fontSize={48} />
          </div>
        </div>

        <Text className={styles.appTitle}>Something unexpected happened</Text>

        <Text className={styles.appMessage}>
          We encountered a critical error. The good news is that we've logged it and our team has been notified.
        </Text>

        {errorId && (
          <div className={styles.errorIdWrapper}>
            <Tooltip content={copied ? 'Copied!' : 'Click to copy Error ID'} relationship="label">
              <button className={styles.errorIdChip} onClick={handleCopyErrorId} type="button">
                {copied ? <CheckmarkRegular fontSize={16} /> : <ClipboardRegular fontSize={16} />}
                <span>ID: {errorId}</span>
              </button>
            </Tooltip>
          </div>
        )}

        {(error || errorId) && (
          <div className={styles.errorDetailsSection}>
            <Accordion collapsible>
              <AccordionItem value="details">
                <AccordionHeader className={styles.accordionHeader}>Show technical details</AccordionHeader>
                <AccordionPanel>
                  <div className={styles.codeBlock}>
                    {error?.toString() || 'No error message available'}
                    {error?.stack && (
                      <>
                        {'\n\n'}
                        {error.stack}
                      </>
                    )}
                  </div>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </div>
        )}

        <div className={styles.buttonRow}>
          <Button
            appearance="secondary"
            size="large"
            className={styles.actionButton}
            icon={<ArrowClockwiseRegular />}
            onClick={onReload}
          >
            Reload Page
          </Button>
          <Button
            appearance="primary"
            size="large"
            className={styles.actionButton}
            icon={<ArrowCounterclockwiseRegular />}
            onClick={onRetry}
          >
            Try Again
          </Button>
        </div>
      </div>
    </div>
  );
};

type FeatureFallbackProps = {
  onRetry: () => void;
};

const FeatureFallback = ({ onRetry }: FeatureFallbackProps) => {
  const styles = useStyles();

  return (
    <div className={styles.featureContainer}>
      <div className={styles.featureIconRow}>
        <DismissRegular fontSize={20} />
      </div>
      <Text className={styles.featureTitle}>Component Error</Text>
      <Text className={styles.featureMessage}>
        This section couldn't be loaded. Try reloading it.
      </Text>
      <Button
        size="small"
        appearance="subtle"
        icon={<ArrowCounterclockwiseRegular />}
        onClick={onRetry}
        className={styles.retryButton}
      >
        Retry Component
      </Button>
    </div>
  );
};
